import {
  CalendarClock,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ListChecks,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { EmployeeJobCard } from '@/components/employee/EmployeeJobCard';
import { EmployeePage } from '@/components/employee/EmployeePage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { KpiCard } from '@/components/ui/kpi-card';
import { useMySchedule } from '@/hooks/employee-portal';
import { addDays, addMonths, monthGrid, sameMonth, weekRange } from '@/lib/calendar';
import { formatDay, formatDuration, formatWallTime } from '@/lib/dates';
import { todayInBusinessZone } from '@shared/dates';
import type { EmployeeSchedule } from '@shared/schemas/employee-portal';

/*
 * /employee/schedule — ROADMAP.md §6.6, finished by §7.1.
 *
 * **Two hour figures, and the screen names both.** Scheduled is what the office
 * has you down for; clocked is what the clock recorded. This shipped in 6.6 with
 * only the clocked number, because `employee_weekly_schedule` did not exist yet
 * and a screen that says which number it is showing is better than one that
 * guesses — 7.1 created the table and the per-day row was already built for the
 * pair.
 *
 * A day with no hours set says so in the resolver's own words rather than
 * rendering blank: "No working hours are set for Tuesdays". Unavailable is the
 * default (business-rules.md §4), so on a cleaner nobody has scheduled yet that
 * sentence is the whole week, and it names what is missing.
 *
 * Every view fetches exactly its own window (P8): the week asks for seven days,
 * the month grid for its forty-two. Nothing here asks for "everything".
 */

type View = 'week' | 'month' | 'list';

const VIEWS = [
  { value: 'week' as const, label: 'Week' },
  { value: 'month' as const, label: 'Month' },
  { value: 'list' as const, label: 'List' },
];

/** Sunday first, matching `startOfWeek` and `preferred_days` — one convention. */
const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export function SchedulePage() {
  const today = todayInBusinessZone();
  const [view, setView] = useState<View>('week');
  const [anchor, setAnchor] = useState(today);

  const range = view === 'month' ? monthGrid(anchor) : weekRange(anchor);
  const schedule = useMySchedule({ from: range.from, to: range.to });

  function step(direction: -1 | 1): void {
    setAnchor((current) =>
      view === 'month' ? addMonths(current, direction) : addDays(current, direction * 7),
    );
  }

  const heading =
    view === 'month'
      ? new Date(`${anchor.slice(0, 8)}01T00:00:00Z`).toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : `${formatDay(range.from)} – ${formatDay(range.to)}`;

  return (
    <EmployeePage
      title="My Schedule"
      description="Your jobs, the hours you are down for, and the hours you have clocked."
    >
      <div className="mb-4 flex flex-col gap-3">
        <FilterPills label="Choose a view" options={VIEWS} value={view} onChange={setView} />

        <div className="flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label={view === 'month' ? 'Previous month' : 'Previous week'}
            onClick={() => {
              step(-1);
            }}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>

          <p className="min-w-0 flex-1 text-center text-label text-ink-900">{heading}</p>

          <Button
            variant="outline"
            size="icon"
            aria-label={view === 'month' ? 'Next month' : 'Next week'}
            onClick={() => {
              step(1);
            }}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>

        {anchor !== today ? (
          <Button
            variant="ghost"
            onClick={() => {
              setAnchor(today);
            }}
          >
            Back to this {view === 'month' ? 'month' : 'week'}
          </Button>
        ) : null}
      </div>

      {schedule.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load your schedule"
            hint="Something went wrong on our side — this does not mean the week is empty. Try again, and ring the office if it keeps happening."
            onRetry={() => {
              void schedule.refetch();
            }}
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label="Days worked"
              value={schedule.data?.counts.daysWorked ?? null}
              icon={CalendarDays}
              isLoading={schedule.isPending}
              subLabel="Days with a job or a shift"
            />
            <KpiCard
              label="Jobs"
              value={schedule.data?.counts.jobs ?? null}
              icon={ListChecks}
              isLoading={schedule.isPending}
              subLabel={`${String(schedule.data?.counts.completed ?? 0)} completed`}
            />
            {/*
              Scheduled before clocked, because that is the order they are read
              in: what you were down for, then what you did. Both are minutes and
              both format the same way — P26 is the prototype showing `180 min`
              on one screen and `3h` on another for the same figure.
            */}
            <KpiCard
              label="Hours scheduled"
              value={schedule.data?.minutesScheduled ?? null}
              format={formatDuration}
              icon={CalendarClock}
              isLoading={schedule.isPending}
              subLabel="Set by the office, breaks taken off"
            />
            <KpiCard
              label="Hours clocked"
              value={schedule.data?.minutesClocked ?? null}
              format={formatDuration}
              icon={Clock3}
              isLoading={schedule.isPending}
              subLabel="Finished shifts only"
            />
          </div>

          <div className="mt-6">
            {schedule.isPending || !schedule.data ? (
              <div aria-busy="true">
                <span className="sr-only">Loading your schedule</span>
                <div aria-hidden="true" className="h-64 animate-pulse rounded-lg bg-ink-100" />
              </div>
            ) : view === 'month' ? (
              <MonthGrid
                schedule={schedule.data}
                anchor={anchor}
                days={range.days}
                today={today}
                onSelectDay={(day) => {
                  setAnchor(day);
                  setView('week');
                }}
              />
            ) : view === 'list' ? (
              <ListView schedule={schedule.data} />
            ) : (
              <WeekView schedule={schedule.data} days={range.days} today={today} />
            )}
          </div>
        </>
      )}
    </EmployeePage>
  );
}

/**
 * Jobs by day, and the day rows the server already bucketed.
 *
 * **The clocked-minutes bucketing is the server's**, not the browser's. Which
 * calendar day a shift belongs to is a business-zone question — a 6pm Cedar City
 * shift is not tomorrow — and doing it in two places is two chances to do it
 * differently. `EmployeeScheduleDay` carries scheduled and clocked together.
 */
function byDay(schedule: EmployeeSchedule) {
  const jobs = new Map<string, EmployeeSchedule['jobs']>();
  for (const entry of schedule.jobs) {
    jobs.set(entry.scheduledDate, [...(jobs.get(entry.scheduledDate) ?? []), entry]);
  }

  const days = new Map(schedule.days.map((day) => [day.date, day]));

  return { jobs, days };
}

function WeekView({
  schedule,
  days,
  today,
}: {
  schedule: EmployeeSchedule;
  days: string[];
  today: string;
}) {
  const grouped = byDay(schedule);

  return (
    <ul className="space-y-3">
      {days.map((day) => {
        const jobs = grouped.jobs.get(day) ?? [];
        const row = grouped.days.get(day);
        const clocked = row?.minutesClocked ?? 0;
        const scheduled = row?.minutesScheduled ?? 0;
        const isToday = day === today;

        return (
          <li
            key={day}
            className={
              isToday
                ? 'rounded-lg border-2 border-honey-400 bg-white p-4 shadow-card'
                : 'rounded-lg border border-ink-300 bg-white p-4 shadow-card'
            }
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-label text-ink-900">
                {new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
                  weekday: 'long',
                  timeZone: 'UTC',
                })}
                <span className="ml-2 text-meta text-ink-500">{formatDay(day)}</span>
                {isToday ? (
                  <span className="ml-2 rounded-full bg-honey-400 px-2 py-0.5 text-meta text-ink-900">
                    Today
                  </span>
                ) : null}
              </p>
              {/*
                Both figures, in the order they are read: what you were down for,
                then what the clock recorded. Zero is `0m` on either, never a
                dash — a day with no hours is a fact (P1).
              */}
              <p className="text-meta text-ink-500">
                {row?.isAvailable === true ? (
                  <span className="text-ink-700">
                    Scheduled {formatDuration(scheduled)}
                    {row.startTime !== null && row.endTime !== null ? (
                      <span className="text-ink-500">
                        {' '}
                        ({formatWallTime(row.startTime)}–{formatWallTime(row.endTime)})
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <span>Not scheduled</span>
                )}
                <span aria-hidden="true"> · </span>
                {formatDuration(clocked)} clocked
              </p>
            </div>

            {/*
              The resolver's own sentence, shown only when there is nothing on
              the day at all — otherwise it is noise beside a real job. On a
              cleaner nobody has given hours to it reads "No working hours are
              set for Tuesdays", which names what is missing instead of a blank.
            */}
            {row !== undefined && !row.isAvailable && jobs.length === 0 && clocked === 0 ? (
              <p className="mt-2 text-body text-ink-500">{row.explanation}</p>
            ) : jobs.length === 0 ? (
              <p className="mt-2 text-body text-ink-500">
                {clocked > 0 ? 'No jobs booked — hours clocked.' : 'Nothing booked.'}
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {jobs.map((entry) => (
                  <li key={entry.id}>
                    <Link
                      to={`/employee/jobs/${entry.id}`}
                      className="flex min-h-touch items-center gap-2 rounded-sm border border-ink-300 px-3 py-2 text-body hover:border-honey-500"
                    >
                      <span className="shrink-0 text-meta text-ink-500">
                        {formatWallTime(entry.scheduledTime)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-ink-900">
                        {entry.clientName}
                      </span>
                      <span className="shrink-0 text-meta text-ink-500">{entry.statusLabel}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function MonthGrid({
  schedule,
  anchor,
  days,
  today,
  onSelectDay,
}: {
  schedule: EmployeeSchedule;
  anchor: string;
  days: string[];
  today: string;
  /** Tapping a day opens that week. See the note on the small-screen cell. */
  onSelectDay: (day: string) => void;
}) {
  const grouped = byDay(schedule);

  return (
    <div className="overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card">
      <div className="grid grid-cols-7 border-b border-ink-300">
        {WEEKDAY_INITIALS.map((initial, index) => (
          <div
            key={index}
            className="py-2 text-center text-meta text-ink-500"
            // The initials repeat (S, T, S, T), so a screen reader gets the
            // real name rather than a run of ambiguous letters.
            aria-label={
              ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][index]
            }
          >
            {initial}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((day) => {
          const jobs = grouped.jobs.get(day) ?? [];
          const outside = !sameMonth(day, anchor);

          return (
            <div
              key={day}
              className={
                'min-h-[76px] border-b border-r border-ink-300 p-1.5 last:border-r-0 ' +
                (outside ? 'bg-ink-50 text-ink-500' : '')
              }
            >
              <p
                className={
                  day === today
                    ? 'inline-grid size-6 place-items-center rounded-full bg-honey-400 text-meta text-ink-900'
                    : 'text-meta text-ink-500'
                }
              >
                {Number(day.slice(8, 10))}
              </p>

              {/*
                **A month cell is 47px wide at 375px**, which is not enough for
                a chip — "9:0…" is a truncation that tells nobody anything. So
                below `sm` the day carries a count that opens that week, and the
                chips appear only where there is room for them. Same data, two
                densities, rather than one that is unreadable on the screen this
                portal is actually used on.
              */}
              {jobs.length > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    onSelectDay(day);
                  }}
                  aria-label={`${String(jobs.length)} job${jobs.length === 1 ? '' : 's'} on ${formatDay(day)} — open that week`}
                  className="mt-1 w-full rounded-sm bg-honey-50 py-0.5 text-[11px] text-bark-900 sm:hidden"
                >
                  {jobs.length} job{jobs.length === 1 ? '' : 's'}
                </button>
              ) : null}

              <ul className="mt-1 hidden space-y-0.5 sm:block">
                {jobs.slice(0, 2).map((entry) => (
                  <li key={entry.id}>
                    <Link
                      to={`/employee/jobs/${entry.id}`}
                      className="block truncate rounded-sm bg-honey-50 px-1 py-0.5 text-[11px] leading-tight text-bark-900"
                    >
                      {formatWallTime(entry.scheduledTime)} {entry.clientName}
                    </Link>
                  </li>
                ))}
                {jobs.length > 2 ? (
                  <li className="px-1 text-[11px] text-ink-500">+{jobs.length - 2} more</li>
                ) : null}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({ schedule }: { schedule: EmployeeSchedule }) {
  if (schedule.jobs.length === 0) {
    return (
      <div className="rounded-lg border border-ink-300 bg-white shadow-card">
        <EmptyState
          icon={CalendarDays}
          title="Nothing booked this week."
          hint="Jobs the office puts you on appear here. Use the arrows above to look at another week."
        />
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {schedule.jobs.map((entry) => (
        <EmployeeJobCard key={entry.id} job={entry} />
      ))}
    </ul>
  );
}
